// src/pages/Settings.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  LocationOn,
  Security,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Delete,
} from '@mui/icons-material';
import { useLocation } from '../hooks/useLocation';
import { getPrivacySettings, savePrivacySettings, requestLocationPermission } from '../utils/privacy';
import { useAuthStore } from '../stores/authStore';
import { useT } from '../i18n/useT';

export const Settings: React.FC = () => {
  const t = useT();
  const [privacySettings, setPrivacySettings] = useState(getPrivacySettings());
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { location, refresh: refreshLocation } = useLocation();
  const { deleteAccount, authMode } = useAuthStore();

  // Check permission status on mount
  useEffect(() => {
    checkPermissionStatus();
  }, []);

  const checkPermissionStatus = async () => {
    try {
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(permission.state);
      } else {
        // Fallback for browsers without Permissions API
        setPermissionStatus('unknown');
      }
    } catch (error) {
      console.warn('Could not check permission status:', error);
      setPermissionStatus('unknown');
    }
  };

  const handleLocationModeChange = async (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'accuracy' | 'privacy' | null,
  ) => {
    if (newMode === null) return;
    
    setIsUpdating(true);
    setUpdateSuccess(false);

    try {
      const newSettings = {
        ...privacySettings,
        useCoarseLocation: newMode === 'privacy',
        allowPreciseLocation: newMode === 'accuracy',
      };

      // Save settings
      savePrivacySettings(newSettings);
      setPrivacySettings(newSettings);

      // If switching to accuracy mode and we don't have permission, request it
      if (newMode === 'accuracy' && permissionStatus !== 'granted') {
        try {
          await requestLocationPermission();
          await checkPermissionStatus();
          refreshLocation();
        } catch (error) {
          console.warn('Failed to get location permission:', error);
        }
      }

      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRequestPermission = async () => {
    setIsUpdating(true);
    try {
      await requestLocationPermission();
      await checkPermissionStatus();
      refreshLocation();
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      console.error('Permission request failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAccount = async () => {
    await deleteAccount();
    setShowDeleteDialog(false);
  };

  const currentMode = privacySettings.allowPreciseLocation ? 'accuracy' : 'privacy';

  const getPermissionIcon = () => {
    switch (permissionStatus) {
      case 'granted':
        return <CheckCircle color="success" />;
      case 'denied':
        return <Error color="error" />;
      case 'prompt':
        return <Warning color="warning" />;
      default:
        return <Security color="action" />;
    }
  };

  const getPermissionText = () => {
    switch (permissionStatus) {
      case 'granted':
        return 'Location access granted';
      case 'denied':
        return 'Location access denied';
      case 'prompt':
        return 'Location permission needed';
      default:
        return 'Location permission status unknown';
    }
  };

  const getPermissionColor = () => {
    switch (permissionStatus) {
      case 'granted':
        return 'success';
      case 'denied':
        return 'error';
      case 'prompt':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        
        {t("Settings")}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        
        {t("Manage your privacy preferences and location settings")}
      </Typography>

      {updateSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          
          {t("Settings updated successfully!")}
        </Alert>
      )}

      {/* Location Privacy Settings */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LocationOn sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h5">
            
            {t("Location Privacy")}
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          
          {t("Choose how precise your location data should be when getting tour information.")}
        </Typography>

        {/* Location Mode Toggle */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            
            {t("Location Mode")}
          </Typography>
          
          <ToggleButtonGroup
            value={currentMode}
            exclusive
            onChange={handleLocationModeChange}
            aria-label={t("location mode")}
            disabled={isUpdating}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="accuracy" aria-label={t("accuracy mode")}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  
                  {t("Accuracy")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  
                  {t("High precision")}
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="privacy" aria-label={t("privacy mode")}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  
                  {t("Privacy")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  
                  {t("Low precision")}
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Mode Description */}
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              {currentMode === 'accuracy' ? (
                <Box>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    
                    {t("High Precision Mode")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Uses exact GPS coordinates for maximum accuracy")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Provides the most relevant and detailed tour information")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Location data is precise to within a few meters")}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle1" color="secondary" gutterBottom>
                    
                    {t("Privacy Mode")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Uses rounded coordinates for enhanced privacy")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Provides general area information with lower accuracy")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("• Location data is approximate to within ~100 meters")}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Permission Status */}
        <Box>
          <Typography variant="h6" gutterBottom>
            
            {t("Permission Status")}
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                {getPermissionIcon()}
              </ListItemIcon>
              <ListItemText 
                primary={getPermissionText()}
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={permissionStatus.charAt(0).toUpperCase() + permissionStatus.slice(1)}
                      size="small"
                      color={getPermissionColor() as any}
                      variant="outlined"
                    />
                    {location && (
                      <Chip 
                        label={`Current: ${location.latitude.toFixed(currentMode === 'accuracy' ? 6 : 3)}, ${location.longitude.toFixed(currentMode === 'accuracy' ? 6 : 3)}`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          </List>

          {permissionStatus !== 'granted' && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleRequestPermission}
                disabled={isUpdating}
                startIcon={<LocationOn />}
              >
                
                {t("Request Location Permission")}
              </Button>
            </Box>
          )}

          {permissionStatus === 'denied' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              
              {t("Location access has been denied. To use location features, please enable location access in your browser settings and refresh the page.")}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={refreshLocation}
              disabled={isUpdating}
              startIcon={<Refresh />}
              size="small"
            >
              
              {t("Refresh Location")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Additional Privacy Settings */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          
          {t("Additional Privacy Options")}
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={privacySettings.stripImageMetadata}
              onChange={(e) => {
                const newSettings = {
                  ...privacySettings,
                  stripImageMetadata: e.target.checked,
                };
                savePrivacySettings(newSettings);
                setPrivacySettings(newSettings);
              }}
            />
          }
          label={t("Strip metadata from uploaded images")}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
          
          {t("Automatically removes GPS and other metadata from photos for privacy")}
        </Typography>
      </Paper>

      {/* Account Management */}
      {authMode === 'registered' && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            
            {t("Account Management")}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            
            {t("Manage your account settings and data.")}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowDeleteDialog(true)}
              startIcon={<Delete />}
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                },
              }}
            >
              
              {t("Delete Account")}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              
              {t("Permanently delete your account and all associated data")}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>{t("Delete Account")}</DialogTitle>
        <DialogContent>
          <Typography color="error" gutterBottom>
            
            {t("This action cannot be undone.")}
          </Typography>
          <Typography>
            
            {t("Are you sure you want to permanently delete your account? This will:")}
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li">{t("Delete all your conversations")}</Typography>
            <Typography component="li">{t("Remove all your data from our servers")}</Typography>
            <Typography component="li">{t("Cancel any active subscriptions")}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>{t("Cancel")}</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            
            {t("Delete Account")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};